import { Surreal } from 'surrealdb'
import { readonly, ref } from 'vue'

/**
 * The only thing between the browser and the data. There is no backend: the
 * app talks to SurrealDB over a WebSocket, and *authorization is the schema*
 * (see db/schema — row-level PERMISSIONS on every table). Never add an
 * endpoint here that the database itself would not allow.
 */

const endpoint = import.meta.env.VITE_SURREAL_URL
const namespace = import.meta.env.VITE_SURREAL_NAMESPACE
const database = import.meta.env.VITE_SURREAL_DATABASE

/** The record access method defined in db/schema/0001_identity.surql. */
const ACCESS = 'person'

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected'

const state = ref<ConnectionState>('idle')

/** Reactive connection state — the offline banner reads this. */
export const connection = readonly(state)

export const db = new Surreal()

db.subscribe('connected', () => (state.value = 'connected'))
db.subscribe('disconnected', () => (state.value = 'disconnected'))

let connecting: Promise<void> | undefined

/**
 * Connect once, reusing the attempt if several callers race. The driver
 * reconnects on its own after a drop, so this is only about the first hello.
 */
export function connect(): Promise<void> {
  connecting ??= (async () => {
    state.value = 'connecting'
    await db.connect(endpoint, { namespace, database })
  })().catch((error: unknown) => {
    connecting = undefined
    state.value = 'disconnected'
    throw error
  })

  return connecting
}

/** Sign in as one of the two of us. Returns the access token's lifetime owner. */
export async function signIn(email: string, password: string): Promise<void> {
  await connect()
  await db.signin({ access: ACCESS, variables: { email, pass: password } })
}

/** Re-use a token we already hold (e.g. from a previous launch). */
export async function resume(token: string): Promise<void> {
  await connect()
  await db.authenticate(token)
}

export async function signOut(): Promise<void> {
  await db.invalidate()
}
